import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { Select } from "@package/subframe/components/Select";
import {
  FeatherAlertTriangle,
  FeatherPlus,
  FeatherSnowflake,
} from "@subframe/core";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import FridgeTempLogForm from "@/components/logs/FridgeTempLogForm";
import LogEntryCard from "@/components/logs/LogEntryCard";
import { useListColdStorageQueryControllerList } from "@/generated/cold-storage/cold-storage";
import type { LogEntryResponseDto } from "@/generated/model";
import { useOfflineOpenIncidents } from "@/hooks/use-offline-open-incidents";

/**
 * Fridge-temperature page panel: a unit dropdown owns the focus, the entries
 * list is filtered to that unit, and the entry form is locked to that unit.
 *
 * Why unit-first: a nursery can have many fridges and freezers. Showing all
 * entries from all units in one list makes it impossible to tell at a glance
 * whether *this* fridge has been checked today. The dropdown puts the
 * practitioner in front of one unit at a time.
 */
interface FridgeTempPanelProps {
  nurseryId: string;
  entries: LogEntryResponseDto[];
  entriesLoading: boolean;
  initialColdStorageId?: string;
  onSubmit: (args: {
    coldStorageId: string;
    coldStorageName: string;
    temperature: number;
    notes?: string;
  }) => Promise<void>;
  isSubmitting: boolean;
}

export default function FridgeTempPanel({
  nurseryId,
  entries,
  entriesLoading,
  initialColdStorageId,
  onSubmit,
  isSubmitting,
}: FridgeTempPanelProps) {
  const { data: unitsResponse, isLoading: unitsLoading } =
    useListColdStorageQueryControllerList(nurseryId, {
      includeInactive: false,
    });
  const units = unitsResponse?.data ?? [];

  const [coldStorageId, setColdStorageId] = useState<string>(
    initialColdStorageId ?? "",
  );
  const [showForm, setShowForm] = useState(!!initialColdStorageId);

  // Default-select the first unit once the list loads, so the page is useful
  // straight away instead of showing an empty state until the user picks.
  useEffect(() => {
    const first = units[0];
    if (!coldStorageId && first) {
      setColdStorageId(first.id);
    }
  }, [units, coldStorageId]);

  // Pre-select honoured if the URL had one (deep-link).
  useEffect(() => {
    if (initialColdStorageId && initialColdStorageId !== coldStorageId) {
      setColdStorageId(initialColdStorageId);
      setShowForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialColdStorageId]);

  const selectedUnit = useMemo(
    () => units.find((u) => u.id === coldStorageId),
    [units, coldStorageId],
  );

  // Open-incident awareness (NAU-219d). Cached at login + kept warm by the
  // dashboard widget; works offline so a practitioner can see "this fridge
  // is already flagged" even with no signal.
  const { items: openIncidents } = useOfflineOpenIncidents(nurseryId);
  const openIncidentForUnit = useMemo(
    () =>
      coldStorageId
        ? openIncidents.find((i) => i.coldStorageId === coldStorageId)
        : undefined,
    [openIncidents, coldStorageId],
  );

  const filteredEntries = useMemo(
    () =>
      entries.filter(
        (e) => e.coldStorage?.id != null && e.coldStorage.id === coldStorageId,
      ),
    [entries, coldStorageId],
  );

  const handleSubmit = async (args: {
    temperature: number;
    notes?: string;
  }) => {
    if (!selectedUnit) return;
    await onSubmit({
      coldStorageId: selectedUnit.id,
      coldStorageName: selectedUnit.name,
      ...args,
    });
    setShowForm(false);
  };

  if (unitsLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <span className="text-body font-body text-subtext-color">
          Loading fridges…
        </span>
      </div>
    );
  }

  if (units.length === 0) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-neutral-300 py-12">
        <FeatherSnowflake className="h-8 w-8 text-subtext-color" />
        <span className="text-body font-body text-default-font">
          No fridges or freezers configured for this nursery
        </span>
        <span className="text-caption font-caption text-subtext-color">
          A manager needs to add them in the nursery settings before logs can be
          recorded.
        </span>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full items-end gap-3">
        <div className="grow">
          <Select
            label="Fridge or freezer"
            placeholder="Select a unit"
            value={coldStorageId || undefined}
            onValueChange={(v) => setColdStorageId(v)}
          >
            {units.map((unit) => (
              <Select.Item key={unit.id} value={unit.id}>
                {unit.name} ({unit.type === "FREEZER" ? "Freezer" : "Fridge"} ·{" "}
                {unit.targetTempMin}°C to {unit.targetTempMax}°C)
              </Select.Item>
            ))}
          </Select>
        </div>
        <Button
          icon={showForm ? undefined : <FeatherPlus />}
          onClick={() => setShowForm((s) => !s)}
          variant={showForm ? "neutral-secondary" : "brand-primary"}
          size="medium"
          disabled={!selectedUnit}
        >
          {showForm ? "Cancel" : "Add reading"}
        </Button>
      </div>

      {openIncidentForUnit && (
        <Link
          to="/incidents/$incidentId"
          params={{ incidentId: openIncidentForUnit.id }}
          className="flex w-full items-start gap-3 rounded-md border border-warning-300 bg-warning-50 p-3 hover:border-warning-400 hover:shadow-sm transition"
        >
          <FeatherAlertTriangle className="mt-0.5 h-5 w-5 flex-none text-warning-800" />
          <div className="flex grow flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Open incident on this fridge
              </span>
              <Badge variant="warning">
                {openIncidentForUnit.firstReading.temperature}°C
              </Badge>
            </div>
            <span className="text-caption font-caption text-subtext-color">
              Logged readings still go through — a manager needs to review and
              sign this off.
            </span>
          </div>
          <span className="text-caption font-caption text-brand-700 self-center">
            Review →
          </span>
        </Link>
      )}

      {showForm && selectedUnit && (
        <div className="w-full rounded-lg border border-neutral-200 p-4">
          <FridgeTempLogForm
            unit={selectedUnit}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        </div>
      )}

      {entriesLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <span className="text-body font-body text-subtext-color">
            Loading entries…
          </span>
        </div>
      ) : filteredEntries.length === 0 ? (
        <div className="flex w-full flex-col items-center justify-center gap-2 py-12">
          <span className="text-body font-body text-subtext-color">
            {selectedUnit
              ? `No readings recorded for ${selectedUnit.name} today`
              : "Select a fridge to see today's readings"}
          </span>
          {selectedUnit && !showForm && (
            <Button
              icon={<FeatherPlus />}
              onClick={() => setShowForm(true)}
              variant="brand-secondary"
              size="medium"
            >
              Add first reading
            </Button>
          )}
        </div>
      ) : (
        <div className="flex w-full flex-col items-start gap-3">
          {filteredEntries.map((entry) => (
            <LogEntryCard key={entry.id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}
